import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
    ArrowLeft, Pencil, Building2, Mail, Phone, Globe2, Clock, Languages,
    ShieldOff, Sparkles, Tag, Check, LayoutGrid, Wallet, Lock, Link2, LifeBuoy, MapPin,
} from 'lucide-react';
import { getTenantById, getTenantFeatures, getFeatureCatalogue } from '../redux/tenantSlice';
import { SUPPORT_SLA_OPTIONS } from '../redux/featureSlice';
import CommonLoader from '../components/CommonLoader';
import ErrorPage from '../components/ErrorPage';
import useCanManage from '../hooks/useCanManage';
import { unlessSystemId } from '../utils/systemId';
import { formatTime } from '../utils/clock';

// Read-only tenant summary (super_admin) — /tenants/:tenantId/view.
//
// Answers "what did we sell this hospital, and for how much?" without the risk
// of the edit form: nothing here writes. It exists because reading that answer
// off TenantForm meant opening a dirty-tracking form with live toggles just to
// look at it, which is how a service gets flipped by accident.
//
// The PLAN VALUE block is internal commercial data. This page is behind the
// super-admin portal; the same figures must never be rendered on any surface a
// tenant admin can reach.

const TENANT_DOMAIN = import.meta.env.VITE_TENANT_DOMAIN || 'mitra-one-care.com';

const inrFormatter = new Intl.NumberFormat('en-IN', {
    style: 'currency', currency: 'INR', maximumFractionDigits: 0,
});
const inr = (n) => inrFormatter.format(Number(n) || 0);

const Card = ({ title, icon: Icon, children, testid }) => (
    <div data-testid={testid} className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
        {title && (
            <h2 className="flex items-center gap-2 text-[11px] font-extrabold uppercase tracking-widest text-[#2D8A8A] mb-3.5">
                {Icon && <Icon className="w-3.5 h-3.5" />}
                {title}
            </h2>
        )}
        {children}
    </div>
);

const Field = ({ label, icon: Icon, children, mono }) => (
    <div className="min-w-0">
        <p className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-500 mb-0.5">
            {Icon && <Icon className="w-3 h-3 flex-none text-gray-400" />}
            {label}
        </p>
        <p className={`text-sm text-gray-800 break-words ${mono ? 'font-mono' : ''}`}>{children ?? '—'}</p>
    </div>
);

const Pill = ({ tone = 'teal', icon: Icon, children }) => {
    const tones = {
        teal: 'bg-[#E7F5F4] text-[#2D8A8A] border-[#4ABDBD]',
        gold: 'bg-[#FBF1E4] text-[#7A5320] border-[#D7A05C]',
        green: 'bg-green-50 text-green-700 border-green-300',
        gray: 'bg-gray-100 text-gray-500 border-gray-300',
    };
    return (
        <span className={`inline-flex items-center gap-1.5 text-[11px] font-bold border rounded-full px-3 py-1 ${tones[tone] || tones.teal}`}>
            {Icon && <Icon className="w-3 h-3 flex-none" />}
            {children}
        </span>
    );
};

export default function TenantView() {
    const { t } = useTranslation(['tenantpage']);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    // Write-side controls hide without the manage key — the API refuses
    // them anyway (hooks/useCanManage.js).
    const canManage = useCanManage('tenants.manage');
    const { tenantId } = useParams();

    const stateTenantRaw = useSelector((s) => s.tenant?.tenant);
    const loading = useSelector((s) => s.tenant?.loading);
    const featuresLoading = useSelector((s) => s.tenant?.featuresLoading);
    const features = useSelector((s) => s.tenant?.features);
    const tenantServices = useSelector((s) => s.tenant?.tenantServices);
    const tenantMeta = useSelector((s) => s.tenant?.tenantMeta);
    const featureGroups = useSelector((s) => s.tenant?.featureGroups);
    const servicesCatalogue = useSelector((s) => s.tenant?.servicesCatalogue);
    const globalCoreIds = useSelector((s) => s.tenant?.globalCoreIds);
    const serviceCoreIds = useSelector((s) => s.tenant?.serviceCoreIds);
    const tenantTypes = useSelector((s) => s.tenant?.tenantTypes);

    const raw = stateTenantRaw?.tenant || stateTenantRaw;
    // The slice keeps whichever tenant was loaded last, so a row left over from
    // another page must not be shown as this one.
    const tenant = raw?.tenantId === tenantId ? raw : null;

    // `settled` rather than `loading`: on the very first render nothing has been
    // dispatched yet, so loading is still false and a bare `!tenant` check would
    // flash the not-found page before the request even leaves.
    const [settled, setSettled] = useState(false);
    useEffect(() => {
        setSettled(false);
        dispatch(getFeatureCatalogue());
        dispatch(getTenantFeatures(tenantId));
        dispatch(getTenantById(tenantId)).finally(() => setSettled(true));
    }, [dispatch, tenantId]);

    const serviceMap = useMemo(
        () => Object.fromEntries((servicesCatalogue || []).map((s) => [s.id, s])),
        [servicesCatalogue]
    );

    // Same three inputs the form uses to decide "on": the locked cores, whether
    // the owning service is bought, and the saved features map. A service's core
    // features are the service — they are on whenever it is, with no row of
    // their own in `features`.
    const isServiceOn = (svcId) => !svcId || svcId === 'common' || Boolean(tenantServices?.[svcId]);
    const isFeatureOn = (featureId, svcId) => {
        if (!isServiceOn(svcId)) return false;
        const locked = (globalCoreIds || []).includes(featureId)
            || (svcId && svcId !== 'common' && ((serviceCoreIds || {})[svcId] || []).includes(featureId));
        return locked || Boolean(features?.[featureId]);
    };

    const coreGroup = (featureGroups || []).find((g) => g.service === null);
    const serviceGroups = (featureGroups || []).filter((g) => g.service && g.service !== 'common');
    const commonGroup = (featureGroups || []).find((g) => g.service === 'common');

    // The tenant TYPE is a descriptive classification only — a name and a
    // sentence. It owns no services and gates nothing, so nothing on this page
    // is derived from it; the services block below reads `tenantServices`.
    // Falls back to the raw stored value only when it is a readable preset slug
    // ('hospital'); a generated id (TTY00001) is never drawn on screen (owner
    // rule, 29 Sep 2026), so a removed type then shows no pill at all.
    const rawTenantType = tenantMeta?.tenantType || tenant?.tenantType;
    const tenantType = (tenantTypes || []).find((tt) => tt.id === rawTenantType);
    const typeLabel = tenantType?.label || tenantType?.name
        || unlessSystemId(rawTenantType);

    // The Mitra One Care open-platform tenant is the platform's own tenant, not
    // a customer, so it is never quoted and the backend never persists a
    // planPricing for it. Read from the same tenantMeta the "Open-platform
    // tenant" pill above uses, so the two can't contradict each other.
    const isOpenPlatformTenant = Boolean(tenantMeta?.isOpenPlatform);

    // Comes back on both GET /tenants/:id and GET /tenants/:id/features; the
    // features response is the fresher of the two after a save.
    const pricing = tenantMeta?.planPricing || tenant?.planPricing || null;
    const discountText = pricing?.discountType === 'percentage'
        ? t('tenantpage.discount_pct_off', '{{v}}% off', { v: pricing.discountValue })
        : pricing?.discountType === 'amount'
            ? t('tenantpage.discount_amt_off', '{{v}} off', { v: inr(pricing.discountValue) })
            : t('tenantpage.no_discount', 'No discount');

    if (!tenant && (!settled || loading)) {
        return (
            <div data-testid="tenant-view-loading" className="flex flex-col items-center justify-center py-12">
                <CommonLoader />
                <span className="text-xl text-[#4ABDBD]">{t('tenantpage.loading', 'Loading...')}</span>
            </div>
        );
    }
    if (!tenant) {
        return <ErrorPage error={{ status: 404 }} pageName={t('tenantpage.tenant', 'Tenant')} />;
    }

    const isActive = tenant.status === 'active';

    // Hospital-level support SLA. The features response is the fresher source
    // when it carries the key; the tenant row is the fallback. null/'' means
    // the platform default from Settings → Support applies.
    const supportLevel = tenantMeta?.supportLevel !== undefined ? tenantMeta.supportLevel : tenant.supportLevel;
    const supportSlaOption = SUPPORT_SLA_OPTIONS.find((o) => o.value === supportLevel);
    const supportSlaLabel = supportLevel
        ? (supportSlaOption ? t(supportSlaOption.labelKey, supportSlaOption.labelFallback) : supportLevel)
        : t('tenantpage.support_sla_default', 'Platform default');

    return (
        <div data-testid="tenant-view-page">
            {/* ---------------- header ---------------- */}
            <div className="flex items-center gap-3 flex-wrap mb-4">
                <button type="button" data-testid="tenant-view-back"
                    onClick={() => navigate('/tenants')}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-gray-300 text-sm text-gray-600 hover:bg-gray-50 hover:text-[#2D8A8A] cursor-pointer">
                    <ArrowLeft className="w-4 h-4" />
                    {t('tenantpage.back', 'Back')}
                </button>
                <h1 className="text-xl font-bold text-[#1a365d] flex items-center gap-2 min-w-0">
                    <Building2 className="w-5 h-5 text-[#4ABDBD] flex-none" />
                    <span className="truncate">{tenant.name || t('moc.hr_detail_hospital', 'Hospital')}</span>
                </h1>
                {canManage && (
                <button type="button" data-testid="tenant-view-edit"
                    onClick={() => navigate(`/tenants/${tenantId}/edit`, { state: { tenant } })}
                    className="ml-auto flex items-center gap-1.5 px-4 py-2 text-sm rounded-xl bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white font-semibold shadow-lg hover:shadow-xl transition-all cursor-pointer">
                    <Pencil className="w-4 h-4" />
                    {t('tenantpage.edit', 'Edit')}
                </button>
                )}
            </div>

            <div className={`flex items-center gap-2 flex-wrap ${tenantType?.description ? 'mb-2' : 'mb-5'}`}>
                <Pill tone={isActive ? 'green' : 'gray'}>
                    {isActive ? t('tenantpage.active', 'Active') : t('tenantpage.inactive', 'Inactive')}
                </Pill>
                {typeLabel && <Pill tone="teal" icon={LayoutGrid}>{typeLabel}</Pill>}
                <Pill tone="gray" icon={tenantMeta?.connectionMode === 'isolated' ? ShieldOff : Globe2}>
                    {tenantMeta?.connectionMode === 'isolated'
                        ? t('tenantpage.mode_isolated_title', 'Isolated')
                        : t('tenantpage.mode_connected_title', 'MOC-connected')}
                </Pill>
                {tenantMeta?.subdomain && (
                    <Pill tone="gray" icon={Link2}>
                        <span className="font-mono">{tenantMeta.subdomain}.{TENANT_DOMAIN}</span>
                    </Pill>
                )}
                {/* Where this tenant's people sign in: temp mode on the shared host
                    (the default) or its own address only. */}
                {tenantMeta?.hostMode === 'subdomain' ? (
                    <Pill tone="teal" icon={Link2}>
                        <span data-testid="tenant-host-pill">{t('tenantpage.host_pill_subdomain', 'Signs in at')} <span className="font-mono">{tenantMeta.appUrl || `https://${tenantMeta.subdomain}.${TENANT_DOMAIN}`}</span></span>
                    </Pill>
                ) : (
                    <Pill tone="gray" icon={Link2}>
                        <span data-testid="tenant-host-pill">{t('tenantpage.host_pill_shared', 'Temp mode — signs in at')} <span className="font-mono">app.{TENANT_DOMAIN}</span></span>
                    </Pill>
                )}
                {tenantMeta?.isOpenPlatform && (
                    <Pill tone="gold" icon={Sparkles}>
                        {t('tenantpage.open_platform_tenant', 'Open-platform tenant')}
                    </Pill>
                )}
            </div>

            {tenantType?.description && (
                <p data-testid="tenant-view-type-desc" className="mb-5 max-w-2xl text-[12px] leading-snug text-gray-500">
                    <span className="font-semibold text-gray-600">{typeLabel}</span>
                    {' — '}
                    {tenantType.description}
                </p>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                {/* ---------------- contact ---------------- */}
                <Card testid="tenant-view-contact" title={t('tenantpage.contact', 'Contact')} icon={Mail}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Field label={t('tenantpage.contact_email', 'Contact email')} icon={Mail}>
                            {tenant.contactEmail}
                        </Field>
                        <Field label={t('tenantpage.contact_phone', 'Contact phone')} icon={Phone}>
                            {tenant.contactPhone}
                        </Field>
                        <Field label={t('tenantpage.country', 'Country')} icon={Globe2}>
                            {tenant.country?.countryName || null}
                        </Field>
                        <div data-testid="tenant-view-city">
                            <Field label={t('tenantpage.city', 'City')} icon={MapPin}>
                                {tenant.city || null}
                            </Field>
                        </div>
                        <Field label={t('tenantpage.timezone', 'Timezone')} icon={Clock}>
                            {tenant.timeZone}
                        </Field>
                        <Field label={t('tenantpage.language', 'Language')} icon={Languages}>
                            {tenant.lang}
                        </Field>
                        <div data-testid="tenant-view-support-sla">
                            <Field label={t('tenantpage.support_sla', 'Support SLA')} icon={LifeBuoy}>
                                {supportSlaLabel}
                            </Field>
                        </div>
                    </div>
                </Card>

                {/* ---------------- plan value (INTERNAL) ---------------- */}
                <Card testid="tenant-view-pricing" title={t('tenantpage.plan_value', 'Plan value')} icon={Wallet}>
                    {/* Not "No plan value recorded" for the open-platform tenant:
                        that empty state means "somebody forgot to price this",
                        and here there is deliberately nothing to price. The
                        "super admin only" tag goes with the figures it warns
                        about, so it goes too. */}
                    {isOpenPlatformTenant ? (
                        <p data-testid="tenant-view-open-platform-pricing"
                            className="flex items-center gap-1.5 text-sm text-gray-500">
                            <Sparkles className="w-4 h-4 flex-none text-[#D7A05C]" />
                            {t('tenantpage.open_platform_no_price', 'Mitra One Care platform tenant — no plan value')}
                        </p>
                    ) : (
                        <>
                            <p className="flex items-center gap-1.5 text-[9px] font-extrabold uppercase tracking-widest text-[#D7A05C] mb-3">
                                <Tag className="w-3 h-3 flex-none" />
                                {t('tenantpage.pricing_internal', 'Super Admin only — not shown to the tenant')}
                            </p>
                            {pricing ? (
                                <>
                                    <div className="grid grid-cols-2 gap-4">
                                        <Field label={t('tenantpage.subtotal_monthly', 'Subtotal — monthly')}>
                                            {inr(pricing.subtotalMonthly)}
                                        </Field>
                                        <Field label={t('tenantpage.subtotal_yearly', 'Subtotal — yearly')}>
                                            {inr(pricing.subtotalYearly)}
                                        </Field>
                                        <div className="col-span-2">
                                            <Field label={t('tenantpage.discount', 'Discount')} icon={Tag}>
                                                {discountText}
                                            </Field>
                                        </div>
                                    </div>
                                    <div className="mt-4 pt-3.5 border-t border-gray-100 flex flex-wrap items-baseline gap-x-5 gap-y-1.5 text-[#2D8A8A]">
                                        <span className="text-[10px] font-extrabold uppercase tracking-widest">
                                            {t('tenantpage.plan_total', 'Total')}
                                        </span>
                                        <span>
                                            <b className="text-xl font-extrabold">{inr(pricing.finalMonthly)}</b>
                                            <span className="text-[11px]">/{t('tenantpage.per_month_short', 'mo')}</span>
                                        </span>
                                        <span>
                                            <b className="text-xl font-extrabold">{inr(pricing.finalYearly)}</b>
                                            <span className="text-[11px]">/{t('tenantpage.per_year_short', 'yr')}</span>
                                        </span>
                                    </div>
                                    {pricing.pricedAt && (
                                        <p className="mt-2 text-[11px] text-gray-400">
                                            {t('tenantpage.priced_at', 'Priced')}{' '}
                                            {new Date(pricing.pricedAt).toLocaleDateString()}, {formatTime(pricing.pricedAt)}
                                        </p>
                                    )}
                                </>
                            ) : (
                                <p data-testid="tenant-view-no-pricing" className="text-sm text-gray-400">
                                    {t('tenantpage.no_plan_value', 'No plan value recorded')}
                                </p>
                            )}
                        </>
                    )}
                </Card>
            </div>

            {/* ---------------- services & features ---------------- */}
            <Card testid="tenant-view-services" title={t('tenantpage.services_and_features', 'Services & features')} icon={LayoutGrid}>
                {featuresLoading && !featureGroups?.length ? (
                    <p className="text-sm text-gray-400">{t('tenantpage.loading', 'Loading...')}</p>
                ) : (
                    <div className="flex flex-col gap-3">
                        {coreGroup?.features?.length > 0 && (
                            <div className="rounded-xl border border-gray-200 overflow-hidden">
                                <div className="flex items-center gap-2 px-4 py-2.5 bg-[#F7FBFA]">
                                    <Lock className="w-3.5 h-3.5 text-[#2D8A8A]" />
                                    <h3 className="text-[13px] font-bold text-gray-800">
                                        {t('tenantpage.global_core', 'Global core')}
                                    </h3>
                                    <span className="text-[11px] text-gray-400">
                                        {t('tenantpage.always_on', 'always on')}
                                    </span>
                                </div>
                                <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-5 px-4 py-2.5">
                                    {coreGroup.features.map((f) => (
                                        <li key={f.id} className="flex items-start gap-2 py-1 text-[13px] text-gray-700">
                                            <Check className="w-3.5 h-3.5 mt-0.5 flex-none text-[#4ABDBD]" />
                                            {f.label}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {serviceGroups.map((g) => {
                            const on = isServiceOn(g.service);
                            const enabled = (g.features || []).filter((f) => isFeatureOn(f.id, g.service));
                            return (
                                <div key={g.id} data-testid={`tenant-view-service-${g.service}`}
                                    className={`rounded-xl border overflow-hidden ${on ? 'border-[#4ABDBD]/60' : 'border-gray-200 opacity-70'}`}>
                                    <div className="flex items-center gap-3 px-4 py-2.5 bg-[#F7FBFA] flex-wrap">
                                        <h3 className="text-[13px] font-bold text-gray-800 flex-1 min-w-0">
                                            {serviceMap[g.service]?.label || g.label}
                                        </h3>
                                        <Pill tone={on ? 'green' : 'gray'}>
                                            {on ? t('tenantpage.on', 'On') : t('tenantpage.off', 'Off')}
                                        </Pill>
                                    </div>
                                    {on && (
                                        <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-5 px-4 py-2.5">
                                            {enabled.length > 0 ? enabled.map((f) => (
                                                <li key={f.id} className="flex items-start gap-2 py-1 text-[13px] text-gray-700">
                                                    <Check className="w-3.5 h-3.5 mt-0.5 flex-none text-[#4ABDBD]" />
                                                    {f.label}
                                                </li>
                                            )) : (
                                                <li className="py-1 text-[12px] text-gray-400">
                                                    {t('tenantpage.no_features_enabled', 'No optional features enabled')}
                                                </li>
                                            )}
                                        </ul>
                                    )}
                                </div>
                            );
                        })}

                        {commonGroup?.features?.length > 0 && (
                            <div className="rounded-xl border border-gray-200 overflow-hidden">
                                <div className="px-4 py-2.5 bg-[#F7FBFA]">
                                    <h3 className="text-[13px] font-bold text-gray-800">
                                        {t('tenantpage.common_features', 'Common features')}
                                    </h3>
                                </div>
                                <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-5 px-4 py-2.5">
                                    {commonGroup.features.filter((f) => isFeatureOn(f.id, 'common')).map((f) => (
                                        <li key={f.id} className="flex items-start gap-2 py-1 text-[13px] text-gray-700">
                                            <Check className="w-3.5 h-3.5 mt-0.5 flex-none text-[#4ABDBD]" />
                                            {f.label}
                                        </li>
                                    ))}
                                    {commonGroup.features.every((f) => !isFeatureOn(f.id, 'common')) && (
                                        <li className="py-1 text-[12px] text-gray-400">
                                            {t('tenantpage.no_features_enabled', 'No optional features enabled')}
                                        </li>
                                    )}
                                </ul>
                            </div>
                        )}
                    </div>
                )}
            </Card>
        </div>
    );
}
